import { useState, useEffect } from 'react';
import axios from 'axios';
import { Building2, CheckCircle2, Eye, EyeOff, FileText, Globe, Key, Mail, Plus, Save, Send, Trash2, XCircle } from 'lucide-react';
import { Input } from '../components/shared/UI';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsModule = ({ settings, onSave }) => {
  const [companyData, setCompanyData] = useState(settings || {});
  const [activeTab, setActiveTab] = useState('company');

  useEffect(() => {
    if (settings) {
      // Normalise legacy single-account fields into bankAccounts array.
      let next = { ...settings };
      if ((!next.bankAccounts || next.bankAccounts.length === 0) && (next.bankName || next.bankAccount || next.bankAccountName)) {
        next.bankAccounts = [{
          bankName: next.bankName || '',
          bankAccount: next.bankAccount || '',
          bankAccountName: next.bankAccountName || ''
        }];
      }
      if (!next.bankAccounts) next.bankAccounts = [];
      setCompanyData(next);
    }
  }, [settings]);

  const handleSaveCompany = async () => {
    // Mirror first account into legacy fields so anything still reading them keeps working.
    const accounts = companyData.bankAccounts || [];
    const payload = {
      ...companyData,
      bankAccounts: accounts,
      bankName: accounts[0]?.bankName || '',
      bankAccount: accounts[0]?.bankAccount || '',
      bankAccountName: accounts[0]?.bankAccountName || '',
    };
    await onSave(payload);
  };

  const updateBankAccount = (index, field, value) => {
    const accounts = [...(companyData.bankAccounts || [])];
    accounts[index] = { ...accounts[index], [field]: value };
    setCompanyData({ ...companyData, bankAccounts: accounts });
  };

  const addBankAccount = () => {
    const accounts = [...(companyData.bankAccounts || []), { bankName: '', bankAccount: '', bankAccountName: '' }];
    setCompanyData({ ...companyData, bankAccounts: accounts });
  };

  const removeBankAccount = (index) => {
    const accounts = (companyData.bankAccounts || []).filter((_, i) => i !== index);
    setCompanyData({ ...companyData, bankAccounts: accounts });
  };

  // ---- SMTP tab state & handlers ----
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [smtpTestEmail, setSmtpTestEmail] = useState('');
  const [smtpTestStatus, setSmtpTestStatus] = useState(null); // { type: 'success'|'error', message }
  const [smtpTesting, setSmtpTesting] = useState(false);

  const handleTestSmtp = async () => {
    setSmtpTestStatus(null);
    if (!smtpTestEmail) {
      setSmtpTestStatus({ type: 'error', message: 'Masukkan alamat email tujuan terlebih dahulu' });
      return;
    }
    setSmtpTesting(true);
    try {
      const res = await axios.post(`${API}/smtp/test`, {
        recipientEmail: smtpTestEmail,
        overrideSettings: {
          smtpFromName: companyData.smtpFromName || '',
          smtpFromEmail: companyData.smtpFromEmail || '',
          smtpHost: companyData.smtpHost || '',
          smtpPort: companyData.smtpPort || 587,
          smtpEncryption: companyData.smtpEncryption || 'tls',
          smtpUser: companyData.smtpUser || '',
          smtpPassword: companyData.smtpPassword || '',
        },
      });
      setSmtpTestStatus({ type: 'success', message: res.data?.message || 'Your SMTP details are correct' });
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setSmtpTestStatus({ type: 'error', message: typeof detail === 'string' ? detail : JSON.stringify(detail) });
    } finally {
      setSmtpTesting(false);
    }
  };

  return (
    <div className="space-y-6 animate-in">
      <div>
        <h2 className="text-3xl font-heading font-bold tracking-tight">Pengaturan</h2>
        <p className="text-muted-foreground mt-1">Kelola pengaturan aplikasi</p>
      </div>

      <div className="flex gap-1 sm:gap-2 border-b border-border overflow-x-auto no-scrollbar">
        <button 
          className={`px-4 py-3 font-medium text-sm transition-colors whitespace-nowrap inline-flex items-center gap-2 ${activeTab === 'company' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
          onClick={() => setActiveTab('company')}
          data-testid="tab-company"
        >
          <FileText size={15} /> Perusahaan
        </button>
        <button 
          className={`px-4 py-3 font-medium text-sm transition-colors whitespace-nowrap inline-flex items-center gap-2 ${activeTab === 'payment' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
          onClick={() => setActiveTab('payment')}
          data-testid="tab-payment"
        >
          <Building2 size={15} /> Akun Bank
        </button>
        <button 
          className={`px-4 py-3 font-medium text-sm transition-colors whitespace-nowrap inline-flex items-center gap-2 ${activeTab === 'recaptcha' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
          onClick={() => setActiveTab('recaptcha')}
          data-testid="tab-recaptcha"
        >
          <Key size={15} /> Google Recaptcha
        </button>
        <button 
          className={`px-4 py-3 font-medium text-sm transition-colors whitespace-nowrap inline-flex items-center gap-2 ${activeTab === 'smtp' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
          onClick={() => setActiveTab('smtp')}
          data-testid="tab-smtp"
        >
          <Mail size={15} /> SMTP Email
        </button>
      </div>

      {activeTab === 'company' && (
        <div className="card p-6 space-y-6">
          <h3 className="font-heading font-semibold text-lg border-b border-border pb-4">Informasi Perusahaan</h3>
          
          {/* Logo Upload Section */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
            <div className="flex-shrink-0">
              {companyData.logoImage ? (
                <img 
                  src={companyData.logoImage} 
                  alt="Logo Perusahaan" 
                  className="w-24 h-24 object-contain rounded-xl border border-slate-200 bg-white p-2"
                />
              ) : (
                <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-700 flex items-center justify-center shadow-lg">
                  <FileText className="w-10 h-10 text-white" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-slate-900 mb-1">Logo Perusahaan</h4>
              <p className="text-sm text-muted-foreground mb-3">Logo akan ditampilkan di halaman login dan dokumen. Format: JPG, PNG. Max 2MB.</p>
              <div className="flex flex-wrap gap-2">
                <label className="btn-secondary cursor-pointer">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 2 * 1024 * 1024) {
                        alert('Ukuran file maksimal 2MB');
                        return;
                      }
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setCompanyData({...companyData, logoImage: event.target.result});
                      };
                      reader.readAsDataURL(file);
                    }}
                    data-testid="logo-upload-input"
                  />
                  Upload Logo
                </label>
                {companyData.logoImage && (
                  <button 
                    onClick={() => setCompanyData({...companyData, logoImage: null})}
                    className="btn-secondary text-rose-600 hover:bg-rose-50"
                    data-testid="remove-logo-btn"
                  >
                    Hapus Logo
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Favicon Upload Section */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
            <div className="flex-shrink-0">
              {companyData.faviconImage ? (
                <div className="w-24 h-24 rounded-xl border border-slate-200 bg-white p-2 flex items-center justify-center">
                  <img 
                    src={companyData.faviconImage} 
                    alt="Favicon" 
                    className="w-12 h-12 object-contain"
                  />
                </div>
              ) : (
                <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center shadow-lg">
                  <Globe className="w-9 h-9 text-white" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-slate-900 mb-1">Favicon</h4>
              <p className="text-sm text-muted-foreground mb-3">Favicon akan tampil di tab browser. Disarankan ukuran 32×32 atau 64×64 piksel. Format: ICO, PNG, SVG. Max 512KB.</p>
              <div className="flex flex-wrap gap-2">
                <label className="btn-secondary cursor-pointer">
                  <input
                    type="file"
                    accept="image/x-icon,image/vnd.microsoft.icon,image/png,image/svg+xml,.ico"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 512 * 1024) {
                        alert('Ukuran file maksimal 512KB');
                        return;
                      }
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setCompanyData({...companyData, faviconImage: event.target.result});
                      };
                      reader.readAsDataURL(file);
                    }}
                    data-testid="favicon-upload-input"
                  />
                  Upload Favicon
                </label>
                {companyData.faviconImage && (
                  <button 
                    onClick={() => setCompanyData({...companyData, faviconImage: null})}
                    className="btn-secondary text-rose-600 hover:bg-rose-50"
                    data-testid="remove-favicon-btn"
                  >
                    Hapus Favicon
                  </button>
                )}
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <Input label="Nama Aplikasi" value={companyData.appName || ''} onChange={e => setCompanyData({...companyData, appName: e.target.value})} />
            <Input label="Nama Perusahaan" value={companyData.companyName || ''} onChange={e => setCompanyData({...companyData, companyName: e.target.value})} />
            <Input label="Domain" value={companyData.domain || ''} onChange={e => setCompanyData({...companyData, domain: e.target.value})} />
            <Input label="Telepon" value={companyData.phone || ''} onChange={e => setCompanyData({...companyData, phone: e.target.value})} />
            <Input label="Email" type="email" value={companyData.email || ''} onChange={e => setCompanyData({...companyData, email: e.target.value})} />
          </div>

          <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider pt-4">Alamat</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <div className="md:col-span-2">
              <label className="label">Alamat</label>
              <textarea className="input-field resize-none" rows="2" value={companyData.address || ''} onChange={e => setCompanyData({...companyData, address: e.target.value})} />
            </div>
            <Input label="Kota" value={companyData.city || ''} onChange={e => setCompanyData({...companyData, city: e.target.value})} />
            <Input label="Provinsi" value={companyData.state || ''} onChange={e => setCompanyData({...companyData, state: e.target.value})} />
            <Input label="Kode Pos" value={companyData.zip || ''} onChange={e => setCompanyData({...companyData, zip: e.target.value})} />
          </div>

          <div className="flex justify-end pt-4">
            <button onClick={handleSaveCompany} className="btn-primary" data-testid="save-company-btn">
              <Save size={16}/> Simpan
            </button>
          </div>
        </div>
      )}

      {activeTab === 'payment' && (
        <div className="card p-6 space-y-6">
          <div className="flex items-center justify-between border-b border-border pb-4">
            <div>
              <h3 className="font-heading font-semibold text-lg">Akun Bank</h3>
              <p className="text-sm text-muted-foreground mt-1">Daftar rekening bank yang akan tampil pada Invoice. Anda dapat menambahkan lebih dari 1 akun.</p>
            </div>
            <div className="hidden sm:flex w-10 h-10 rounded-xl bg-primary/10 items-center justify-center">
              <Building2 size={18} className="text-primary" />
            </div>
          </div>

          <div>
            <Input
              label="Metode Pembayaran"
              placeholder="Transfer"
              value={companyData.paymentMethod || ''}
              onChange={e => setCompanyData({ ...companyData, paymentMethod: e.target.value })}
              data-testid="settings-payment-method"
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">Daftar Akun Bank</h4>
              <button
                type="button"
                onClick={addBankAccount}
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="add-bank-account-btn"
              >
                <Plus size={14} /> Tambah Akun Bank
              </button>
            </div>

            {(!companyData.bankAccounts || companyData.bankAccounts.length === 0) ? (
              <div className="text-center py-8 border-2 border-dashed border-border rounded-xl">
                <Building2 size={28} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Belum ada akun bank. Klik &quot;Tambah Akun Bank&quot; untuk menambahkan.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {companyData.bankAccounts.map((acc, idx) => (
                  <div key={idx} className="p-4 border border-border rounded-xl bg-slate-50/50 relative" data-testid={`bank-account-${idx}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold uppercase tracking-wider text-primary">Akun #{idx + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeBankAccount(idx)}
                        className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                        title="Hapus akun bank"
                        data-testid={`remove-bank-account-${idx}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Input
                        label="Nama Bank"
                        placeholder="BCA"
                        value={acc.bankName || ''}
                        onChange={e => updateBankAccount(idx, 'bankName', e.target.value)}
                        data-testid={`bank-name-${idx}`}
                      />
                      <Input
                        label="No. Rekening"
                        placeholder="3980251500"
                        value={acc.bankAccount || ''}
                        onChange={e => updateBankAccount(idx, 'bankAccount', e.target.value)}
                        data-testid={`bank-account-${idx}-no`}
                      />
                      <Input
                        label="Atas Nama"
                        placeholder="PT. ABC"
                        value={acc.bankAccountName || ''}
                        onChange={e => updateBankAccount(idx, 'bankAccountName', e.target.value)}
                        data-testid={`bank-account-name-${idx}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="label">Syarat & Ketentuan</label>
            <textarea
              className="input-field resize-none"
              rows="3"
              placeholder="Pembayaran dilakukan selambat-lambat 3 hari kerja setelah invoice ini dikeluarkan."
              value={companyData.invoiceTerms || ''}
              onChange={e => setCompanyData({ ...companyData, invoiceTerms: e.target.value })}
              data-testid="settings-invoice-terms"
            />
          </div>

          <div className="flex justify-end pt-4 border-t border-border">
            <button onClick={handleSaveCompany} className="btn-primary" data-testid="save-payment-btn">
              <Save size={16}/> Simpan
            </button>
          </div>
        </div>
      )}

      {activeTab === 'recaptcha' && (
        <div className="card p-6 space-y-6">
          <h3 className="font-heading font-semibold text-lg border-b border-border pb-4">Google Recaptcha</h3>
          
          {/* Enable/Disable */}
          <label className="flex items-center gap-3 cursor-pointer" data-testid="recaptcha-toggle">
            <input
              type="checkbox"
              checked={companyData.recaptchaEnabled || false}
              onChange={e => setCompanyData({...companyData, recaptchaEnabled: e.target.checked})}
              className="w-5 h-5 rounded border-2 accent-primary"
            />
            <span className="font-medium">Google Recaptcha</span>
          </label>

          {companyData.recaptchaEnabled && (
            <div className="space-y-6 pt-2">
              {/* Version Selection */}
              <div>
                <p className="text-sm text-muted-foreground mb-3">Choose Google Recaptcha Version</p>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="recaptchaVersion"
                      value="v2"
                      checked={(companyData.recaptchaVersion || 'v2') === 'v2'}
                      onChange={() => setCompanyData({...companyData, recaptchaVersion: 'v2'})}
                      className="w-4 h-4 accent-primary"
                    />
                    <span className="font-medium text-sm">V2</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="recaptchaVersion"
                      value="v3"
                      checked={companyData.recaptchaVersion === 'v3'}
                      onChange={() => setCompanyData({...companyData, recaptchaVersion: 'v3'})}
                      className="w-4 h-4 accent-primary"
                    />
                    <span className="font-medium text-sm">V3</span>
                  </label>
                </div>
              </div>

              {/* Keys */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label">
                    Google Recaptcha {(companyData.recaptchaVersion || 'v2').toUpperCase()} Key <span className="text-destructive">*</span>
                  </label>
                  <input
                    className="input-field"
                    placeholder="6Lc..."
                    value={companyData.recaptchaSiteKey || ''}
                    onChange={e => setCompanyData({...companyData, recaptchaSiteKey: e.target.value})}
                    data-testid="recaptcha-site-key"
                  />
                </div>
                <div>
                  <label className="label">
                    Google Recaptcha {(companyData.recaptchaVersion || 'v2').toUpperCase()} Secret <span className="text-destructive">*</span>
                  </label>
                  <input
                    className="input-field"
                    type="password"
                    placeholder="Secret key..."
                    value={companyData.recaptchaSecretKey || ''}
                    onChange={e => setCompanyData({...companyData, recaptchaSecretKey: e.target.value})}
                    data-testid="recaptcha-secret-key"
                  />
                </div>
              </div>

              <a
                href="https://www.google.com/recaptcha/admin/create"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm transition-colors hover:underline"
                style={{color: '#0091A0'}}
              >
                Generate Credentials
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              </a>
            </div>
          )}

          <div className="flex justify-end pt-4 border-t border-border">
            <button onClick={handleSaveCompany} className="btn-primary" data-testid="save-recaptcha-btn">
              <Save size={16}/> Simpan
            </button>
          </div>
        </div>
      )}

      {activeTab === 'smtp' && (
        <div className="card p-6 space-y-6">
          <div className="flex items-center justify-between border-b border-border pb-4">
            <div>
              <h3 className="font-heading font-semibold text-lg">SMTP Email</h3>
              <p className="text-sm text-muted-foreground mt-1">Konfigurasi server email untuk pengiriman invoice & notifikasi.</p>
            </div>
            <div className="hidden sm:flex w-10 h-10 rounded-xl bg-primary/10 items-center justify-center">
              <Mail size={18} className="text-primary" />
            </div>
          </div>

          {smtpTestStatus && (
            <div
              className={`flex items-start gap-3 p-4 rounded-xl ${smtpTestStatus.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-rose-50 text-rose-800 border border-rose-200'}`}
              data-testid="smtp-test-status"
            >
              {smtpTestStatus.type === 'success' ? <CheckCircle2 size={18} className="flex-shrink-0 mt-0.5" /> : <XCircle size={18} className="flex-shrink-0 mt-0.5" />}
              <span className="text-sm font-medium">{smtpTestStatus.message}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <Input
              label="Mail From Name"
              required
              placeholder="e.g. John Doe"
              value={companyData.smtpFromName || ''}
              onChange={e => setCompanyData({ ...companyData, smtpFromName: e.target.value })}
              data-testid="smtp-from-name"
            />
            <Input
              label="Mail From Email"
              required
              type="email"
              placeholder="e.g. johndoe@example.com"
              value={companyData.smtpFromEmail || ''}
              onChange={e => setCompanyData({ ...companyData, smtpFromEmail: e.target.value })}
              data-testid="smtp-from-email"
            />

            <div>
              <label className="label">Enable Email Queue</label>
              <select
                className="input-field bg-white"
                value={companyData.smtpEnableQueue ? 'yes' : 'no'}
                onChange={e => setCompanyData({ ...companyData, smtpEnableQueue: e.target.value === 'yes' })}
                data-testid="smtp-enable-queue"
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </div>
            <div>
              <label className="label">Mail Driver</label>
              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="smtpDriver"
                    value="mail"
                    checked={(companyData.smtpDriver || 'smtp') === 'mail'}
                    onChange={() => setCompanyData({ ...companyData, smtpDriver: 'mail' })}
                    className="w-4 h-4 accent-primary"
                    data-testid="smtp-driver-mail"
                  />
                  <span className="text-sm">Mail</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="smtpDriver"
                    value="smtp"
                    checked={(companyData.smtpDriver || 'smtp') === 'smtp'}
                    onChange={() => setCompanyData({ ...companyData, smtpDriver: 'smtp' })}
                    className="w-4 h-4 accent-primary"
                    data-testid="smtp-driver-smtp"
                  />
                  <span className="text-sm">SMTP</span>
                </label>
              </div>
            </div>

            <div className="md:col-span-2">
              <Input
                label="Mail Host"
                required
                placeholder="smtp.example.com"
                value={companyData.smtpHost || ''}
                onChange={e => setCompanyData({ ...companyData, smtpHost: e.target.value })}
                data-testid="smtp-host"
              />
            </div>

            <Input
              label="Mail Port"
              required
              type="number"
              placeholder="587"
              value={companyData.smtpPort ?? ''}
              onChange={e => setCompanyData({ ...companyData, smtpPort: e.target.value === '' ? '' : parseInt(e.target.value, 10) })}
              data-testid="smtp-port"
            />
            <div>
              <label className="label">Mail Encryption</label>
              <select
                className="input-field bg-white"
                value={companyData.smtpEncryption || 'tls'}
                onChange={e => setCompanyData({ ...companyData, smtpEncryption: e.target.value })}
                data-testid="smtp-encryption"
              >
                <option value="tls">tls (STARTTLS)</option>
                <option value="ssl">ssl</option>
                <option value="none">none</option>
              </select>
            </div>

            <Input
              label="Mail Username"
              required
              placeholder="e.g. johndoe@example.com"
              value={companyData.smtpUser || ''}
              onChange={e => setCompanyData({ ...companyData, smtpUser: e.target.value })}
              data-testid="smtp-user"
            />
            <div>
              <label className="label">Mail Password</label>
              <div className="relative">
                <input
                  className="input-field pr-12"
                  type={showSmtpPassword ? 'text' : 'password'}
                  placeholder="Kosongkan untuk pakai password tersimpan"
                  value={companyData.smtpPassword || ''}
                  onChange={e => setCompanyData({ ...companyData, smtpPassword: e.target.value })}
                  data-testid="smtp-password"
                />
                <button
                  type="button"
                  onClick={() => setShowSmtpPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                  data-testid="toggle-smtp-password"
                  aria-label="Toggle password visibility"
                >
                  {showSmtpPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
              <div>
                <label className="label">Test ke alamat email</label>
                <input
                  type="email"
                  className="input-field"
                  placeholder="kirim test email ke..."
                  value={smtpTestEmail}
                  onChange={e => setSmtpTestEmail(e.target.value)}
                  data-testid="smtp-test-email"
                />
              </div>
              <button
                type="button"
                onClick={handleTestSmtp}
                disabled={smtpTesting}
                className="btn-outline disabled:opacity-50"
                data-testid="send-test-email-btn"
              >
                <Send size={15} />
                {smtpTesting ? 'Mengirim...' : 'Send Test Email'}
              </button>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-border">
            <button onClick={handleSaveCompany} className="btn-primary" data-testid="save-smtp-btn">
              <Save size={16}/> Simpan
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default SettingsModule;
